import React, { JSX, KeyboardEvent } from "react";

// Define the props interface with generic typing for flexibility
interface LinearSelectorProps<T extends string> {
  options: T[];
  selected: T;
  setSelected: React.Dispatch<React.SetStateAction<T>> | Function;
  onSelect?: (option: T) => void;
}

const LinearSelector = <T extends string>({
  options,
  selected,
  setSelected,
  onSelect
}: LinearSelectorProps<T>): JSX.Element => {
  /**
   * Handles the click event for selecting an option.
   * @param option - The option to select.
   */
  const handleSelect = (option: T) => {
    setSelected(option);
    onSelect && onSelect(option);
  };

  /**
   * Handles the key down event for accessibility.
   * Allows selection via Enter or Space keys.
   * @param event - The keyboard event.
   * @param option - The option to select.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, option: T) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setSelected(option);
    }
  };

  return (
    <div className="flex">
      {options.map((option) => (
        <button
          key={option}
          tabIndex={0}
          className={`text-sm text-neutral-900 py-[10px] border-b-2 ${
            selected === option
              ? "border-neutral-900 font-medium"
              : "border-transparent"
          } mr-6 focus:outline-none focus:border-neutral-900 transition-colors duration-200`}
          onClick={() => handleSelect(option)}
          onKeyDown={(e) => handleKeyDown(e, option)}
          aria-pressed={selected === option}
        >
          {option}
        </button>
      ))}
    </div>
  );
};

export default LinearSelector;
